<div class="dca-heading flex items-center justify-between px2">
  <div class="flex items-center">
    <button
      mat-icon-button
      class="h2"
      matTooltip="Reference list"
      (click)="routeToReferencesList()"
    >
      <mat-icon>keyboard_backspace</mat-icon>
    </button>
    <div>
      <div class="m0 h2">Edit citation</div>
    </div>
  </div>
</div>
<div class="dca-scrollarea p2">
  <h3 class="m0 h4 mb1 text-muted" id="citation-mode-title">Citation Generator</h3>
  <mat-card
    class="p2 mb2 citation-editor-section citation-editor-section--text"
    aria-labelledby="citation-mode-title"
  >
    <mat-tab-group
      class="citation-mode-tabs mb1"
      [selectedIndex]="citationMode.value === 'custom' ? 1 : 0"
      (selectedIndexChange)="onCitationModeTabChange($event)"
      animationDuration="0ms"
      [mat-stretch-tabs]="false"
    >
      <mat-tab label="Generated"></mat-tab>
      <mat-tab label="Custom"></mat-tab>
    </mat-tab-group>

    @if (citationMode.value === 'generated') {
      <div class="citation-preview" aria-label="Citation preview">
        <p class="citation-preview__label m0 text-muted">In-text output</p>
        <p
          class="citation-preview__text m0"
          matTooltip="Rendered citation output for the current style"
        >
          {{ generatedPreviewText || plainCitation.value || 'No generated citation text available yet.' }}
        </p>
        <div class="citation-preview__hint mt1">
          <span>
            Preview based on APA 7th edition. The final output may differ depending on the citation
            style used during export.
          </span>
        </div>
      </div>
    }

    @if (citationMode.value === 'custom') {
      <div class="citation-custom-warning mb1" role="note">
        Custom citations are not updated when the citation style changes. Use Generated mode for
        automatic formatting.
      </div>
      <mat-form-field appearance="outline" floatLabel="always" class="block m0">
        <mat-label>Custom in-text citation</mat-label>
        <sfo-rich-text-input [formControl]="plainCitationHtml"></sfo-rich-text-input>
        <mat-hint>Applies your typed text exactly at export.</mat-hint>
      </mat-form-field>
      <div class="flex items-center justify-end mt2">
        <button
          mat-stroked-button
          type="button"
          class="mr1"
          aria-label="Reset citation text"
          [disabled]="!isCustomTextDirty"
          (click)="resetCitationTextOverride()"
        >
          Reset
        </button>
        <button
          mat-flat-button
          color="primary"
          type="button"
          aria-label="Apply Custom citation text"
          [disabled]="!isCustomTextDirty"
          (click)="applyCitationUpdate({ applyCustomText: true })"
        >
          Apply Custom text
        </button>
      </div>
    }
  </mat-card>

  @if (formattedCitationSuggestion) {
    <mat-card class="p2 mb2 citation-editor-section" aria-label="Formatted citation suggestion">
      <p class="m0 mb1">
        A new formatted citation text is available:
        <b>{{ formattedCitationSuggestion }}</b>
      </p>
      <div class="flex items-center">
        <button
          mat-flat-button
          color="primary"
          type="button"
          class="mr1"
          (click)="applyFormattedCitationSuggestion()"
        >
          Apply
        </button>
        <button mat-button type="button" (click)="discardFormattedCitationSuggestion()">
          Discard
        </button>
      </div>
    </mat-card>
  }

  <section
    class="citation-editor-section citation-editor-section--references"
    aria-labelledby="citation-details-step-title"
  >
    <div class="citation-section-header mb1">
      <h3 class="m0 h4 text-muted" id="citation-details-step-title">
        {{ citationMode.value === 'custom' ? 'Linked references' : 'References in this citation' }}
        ({{ citationItems.length }})
      </h3>
      @if (citationMode.value === 'custom') {
        <p class="m0 citation-section-description">
          These references are associated with this citation for bibliography and XML export.
        </p>
      }
    </div>

    <form [formGroup]="citationFormGroup">
      <ng-container formArrayName="citationItems">
        @if (citationItems.length === 0) {
          <mat-card class="p2 mb2"> No references added yet. Use Add reference below. </mat-card>
        }

        <mat-accordion class="citation-reference-panels" multi>
          @for (citationItem of citationItems.controls; track citationItem; let i = $index) {
            <mat-expansion-panel
              class="citation-item-panel mb1"
              [expanded]="isPanelExpanded(i)"
              (opened)="onPanelOpened(i)"
              (closed)="onPanelClosed(i)"
              [attr.aria-label]="'Citation reference ' + (i + 1)"
            >
              <mat-expansion-panel-header class="citation-item-panel__header">
                <mat-panel-title>
                  <span class="citation-item-panel__index">{{ i + 1 }}</span>
                  <span
                    class="citation-item-panel__title"
                    [matTooltip]="citationItem.get('plainReference')?.value || ('Reference ' + (i + 1))"
                  >
                    {{ citationItem.get('plainReference')?.value || 'Reference ' + (i + 1) }}
                  </span>
                </mat-panel-title>
              </mat-expansion-panel-header>

              <div [formGroupName]="i" class="citation-item-panel__body">
                <div class="citation-row flex mxn1">
                  <mat-form-field class="col-8 px1" appearance="outline">
                    <mat-label>Label</mat-label>
                    <input
                      matInput
                      placeholder="e.g. page, section, chapter"
                      aria-label="Citation label"
                      formControlName="label"
                      [matAutocomplete]="labelAutocomplete"
                    />
                  </mat-form-field>
                  <mat-form-field class="col-4 px1" appearance="outline">
                    <mat-label>Locator</mat-label>
                    <input
                      matInput
                      placeholder="e.g. 12"
                      aria-label="Citation locator"
                      formControlName="locator"
                    />
                  </mat-form-field>
                </div>

                <div class="citation-row flex mxn1">
                  <mat-form-field class="col-6 px1" appearance="outline">
                    <mat-label>Prefix</mat-label>
                    <input
                      matInput
                      placeholder="Text before citation"
                      aria-label="Citation prefix"
                      formControlName="prefix"
                    />
                  </mat-form-field>
                  <mat-form-field class="col-6 px1" appearance="outline">
                    <mat-label>Suffix</mat-label>
                    <input
                      matInput
                      placeholder="Text after citation"
                      aria-label="Citation suffix"
                      formControlName="suffix"
                    />
                  </mat-form-field>
                </div>

                <div class="citation-item-panel__footer px1 mt1">
                  <mat-checkbox formControlName="suppress-author" id="suppress-author-{{ i }}">
                    Suppress author in citation
                  </mat-checkbox>
                  <button
                    mat-icon-button
                    type="button"
                    [attr.aria-label]="'Remove citation reference ' + (i + 1)"
                    matTooltip="Remove this reference from the citation"
                    (click)="deleteReference(i)"
                  >
                    <mat-icon>delete</mat-icon>
                  </button>
                </div>
              </div>
            </mat-expansion-panel>
          }
        </mat-accordion>
      </ng-container>
    </form>
  </section>

  <mat-card
    class="p2 mb2 citation-editor-section citation-editor-section--add-reference"
    aria-labelledby="add-reference-title"
  >
    <h3 class="m0 h4 mb1 text-muted" id="add-reference-title">
      {{ citationMode.value === 'custom' ? 'Link reference' : 'Add reference' }}
    </h3>
    <mat-form-field appearance="outline" class="block m0">
      <mat-label>Search references</mat-label>
      <input
        matInput
        placeholder="Title, creator, year, or ID"
        aria-label="Search references to add to citation"
        #searchField
        (keyup)="searchText$.next(searchField.value)"
        [matAutocomplete]="auto"
      />
      <mat-icon matSuffix matTooltip="Clear search" (click)="searchText$.next('')">add</mat-icon>
    </mat-form-field>
  </mat-card>

  <mat-autocomplete #auto="matAutocomplete">
    @for (reference of references$ | async; track reference) {
      <mat-option (click)="addReference(reference)">
        <small>{{ reference.bib }}</small>
      </mat-option>
    }
  </mat-autocomplete>

  <mat-autocomplete #labelAutocomplete="matAutocomplete">
    @for (label of citationLabelSuggestions; track label) {
      <mat-option [value]="label">{{ label }}</mat-option>
    }
  </mat-autocomplete>
</div>
